import React, { useState } from 'react';
import { Pressable, StyleSheet } from 'react-native';
import { FileType } from '@alucio/aws-beacon-amplify/src/models';
import { DNABox, DNAButton, DNAText, Hoverable, Iffy, luxColors, util } from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import { DNAChip, DNACustomFieldChipList, useDNADocumentActions, DNAFileTypeChip } from 'src/components/DNA';
import { AssociatedFileORM, DocumentORM, getAssociatedFileTitle } from 'src/types/types';
import { useAppSettings } from 'src/state/context/AppSettings';
import { downloadContentFromCloudfront } from 'src/utils/loadCloudfrontAsset/loadCloudfrontAsset';
import { isAssociatedFileAttachment, isPublishedDocumentAttachment } from 'src/types/typeguards';
import { useContent } from 'src/state/context/ContentProvider/ContentProvider';
import { useContentPreviewModal } from '../../State/ContentPreviewModalStateProvider';
import { useAllDocumentsInstance } from 'src/state/redux/selector/document';
import { tabConfigs } from 'src/components/ContentPreviewModalV2/SideBar';
import SidebarContent from 'src/components/ContentPreviewModalV2/SideBar/SideBarContent';

const styles = StyleSheet.create({
  fileRow: {
    paddingBottom: 16,
    borderBottomColor: colors['color-gray-80'],
    borderBottomWidth: 1,
    maxWidth: 280,
  },
  hoveredRow: {
    backgroundColor: luxColors.mainContainer.primary,
  },
  unavailableContent: {
    opacity: 0.4,
  },
})

interface FileRowProps {
  file: AssociatedFileORM,
}

const AssociatedDocumentRow: React.FC<FileRowProps> = ({ file }) => {
  const { isOnline } = useAppSettings()
  const { addPresentation } = useContent()
  const { setIsAssociatedFile } = useContentPreviewModal()
  const [attachedDocumentORM]:(DocumentORM)[] = useAllDocumentsInstance({
    filter: {
      model: { id: file.file?.id },
    },
  })
  const attachedDocumentVersionORM = attachedDocumentORM.relations.version.latestUsableDocumentVersionORM
  const documentActions = useDNADocumentActions()

  const [isHovered, setIsHovered] = useState<boolean>(false)
  const isWebDoc = attachedDocumentORM.model.type === FileType.WEB
  const unavailableContent =
    (!isOnline && !attachedDocumentVersionORM.meta.assets.isContentCached) ||
    (isWebDoc && !isOnline)

  const toggleHover = () => {
    setIsHovered(currVal => !currVal);
  }

  const onFileSelected = () => {
    setIsAssociatedFile(true)
    addPresentation(attachedDocumentVersionORM)
  }
  return (
    <Pressable onPress={onFileSelected} disabled={unavailableContent}>
      <Hoverable onHoverIn={toggleHover} onHoverOut={toggleHover}>
        <DNABox
          childFill
          appearance="col"
          spacing="md"
          style={util.mergeStyles(
            undefined,
            styles.fileRow,
            [styles.hoveredRow, isHovered],
            [styles.unavailableContent, unavailableContent],
          )}
        >
          <DNAText testID="cpm-associated-file-text" style={{ maxWidth: 280 }}>{getAssociatedFileTitle(file)}</DNAText>
          <DNABox
            fill
            spacing="between"
            alignY="end"
          >
            <DNABox spacing="xs">
              <DNACustomFieldChipList documentVersionORM={attachedDocumentVersionORM} />
              <DNAFileTypeChip orm={attachedDocumentORM} />
              <Iffy is={unavailableContent}>
                <DNAChip.Beacon
                  testID={`content-not-available-${attachedDocumentVersionORM.model.title}`}
                  variant="CONTENT_UNAVAILABLE"
                />
              </Iffy>
            </DNABox>
            <Iffy is={attachedDocumentORM.meta.permissions.MSLDownload && !isWebDoc}>
              <DNABox alignX="end" alignY="center">
                <DNAButton
                  appearance="ghost"
                  status="subtle"
                  iconLeft="download-simple"
                  onPress={documentActions.download(attachedDocumentORM)}
                />
              </DNABox>
            </Iffy>
          </DNABox>
        </DNABox>
      </Hoverable>
    </Pressable>
  )
}

const AttachedFileRow: React.FC<FileRowProps> = (props) => {
  const { isOnline } = useAppSettings()
  const { file } = props
  const attachedFile = file.file
  const [isHovered, setIsHovered] = useState<boolean>(false);
  if (!attachedFile || isPublishedDocumentAttachment(attachedFile)) return null

  // TODO CHECK ID FOR ATTACHED DOCUMENTS
  const onFileSelected = () => {
    downloadContentFromCloudfront(attachedFile.srcFile.key, attachedFile.srcFileName, attachedFile.type)
    analytics?.track('DOCUMENT_AF_DOWNLOAD', {
      action: 'AF_DOWNLOAD',
      attachedContentId: file.model.attachmentId,
      category: 'DOCUMENT',
      documentId: file.relations.latestUsableDocumentVersion?.documentId,
      type: file.model.type,
    });
  }
  return (
    <Pressable onPress={onFileSelected} disabled={!isOnline}>
      <Hoverable
        onHoverIn={() => setIsHovered(currVal => !currVal)}
        onHoverOut={() => setIsHovered(currVal => !currVal)}
      >
        <DNABox
          appearance="col"
          spacing="md"
          style={util.mergeStyles(
            undefined,
            styles.fileRow,
            [styles.hoveredRow, isHovered],
            [styles.unavailableContent, !isOnline],
          )}
        >
          <DNAText style={{ maxWidth: 280 }}>{attachedFile.title}</DNAText>
          <DNABox
            fill
            spacing="between"
            alignY="end"
          >
            <DNABox spacing="xs">
              <DNAFileTypeChip orm={file} />
              <Iffy is={!isOnline}>
                <DNAChip.Beacon
                  testID={`content-not-available-${attachedFile.title}`}
                  variant="CONTENT_UNAVAILABLE"
                />
              </Iffy>
            </DNABox>
            <DNABox alignX="end" alignY="end">
              <DNAButton
                appearance="ghost"
                status="subtle"
                iconLeft="download-simple"
                onPress={onFileSelected}
              />
            </DNABox>
          </DNABox>
        </DNABox>
      </Hoverable>
    </Pressable>
  )
}

const AssociatedFiles: React.FC = () => {
  const { activeDocumentVersionORM } = useContentPreviewModal()
  if (!activeDocumentVersionORM) return null

  const versionElements = activeDocumentVersionORM.relations.associatedFiles
    .filter(({ model, file }) => {
      if (isAssociatedFileAttachment(model)) return true
      return isPublishedDocumentAttachment(file)
    })
    .map(assocFile => {
      const { file, model: { id } } = assocFile
      if (isPublishedDocumentAttachment(file)) {
        return (<AssociatedDocumentRow
          file={assocFile}
          key={id}
        />)
      }
      return (<AttachedFileRow
        file={assocFile}
        key={id}
      />)
    })

  return (
    <SidebarContent>
      <SidebarContent.Header
        sidebarHeader={tabConfigs.associatedFiles.label}
      />
      <SidebarContent.Body>
        <DNABox fill appearance="col" spacing="md">
          {versionElements}
        </DNABox>
      </SidebarContent.Body>
    </SidebarContent>
  )
}

export default AssociatedFiles;
